import {
  FlexRow,
  FlexCell,
  IconContainer,
  LabeledInput,
  FileCard,
  DropSpot,
  IconButton,
  Panel,
  i18n,
  PickerInput,
  PickerItem,
  DataPickerRow,
} from '@epam/promo';
import { DataRowProps, useAsyncDataSource } from '@epam/uui';
import {
  getLinkByType,
  removeLink,
  addLink,
  getLinkProps,
} from '../../modules/project-management/details/components/add-to-showroom/helpers/utils';
import { ILens } from '@epam/uui';
import {
  DROP_SPOT,
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_EXT_TYPES,
  fileTypeExtension,
} from '../../modules/project-management/details/components/add-to-showroom/constants';
import {
  IProjectLink,
  IProjectRepofile,
} from 'modules/project-management/models/interfaces';
import { ReactComponent as done } from '@epam/assets/icons/common/notification-done-18.svg';
import { ReactComponent as invalid } from '@epam/assets/icons/common/navigation-close-18.svg';
import { ReactComponent as ClearIcon } from '@epam/assets/icons/common/navigation-close-24.svg';
import useNotifications from 'hooks/useNotifications';
import LabeledInputWithCustomTooltip from 'component/LabeledInputWithCustomTooltip';
import ProjectManagementService from 'services/api/project-management/projectManagementService';
import AddGitlabFile from './AddGitlabFile';
import { ReactComponent as PdfIcon } from '../../assets/svg/pdf.svg';
import { ReactComponent as PptIcon } from '../../assets/svg/ppt.svg';
import { useState } from 'react';
import { IBackendProjectInfoModel } from 'modules/project-list/components/project-card/modal';

const UploadFile = ({
  lens,
  label,
  tooltip,
  className,
  projectLinks,
  targetLinkType,
  isRemovable,
  hideIconContainer,
  info,
  isRequired,
  iconPosition = 'right',
}: {
  lens: ILens<any>;
  label: string;
  tooltip?: string;
  className: string;
  projectLinks: IProjectLink[];
  targetLinkType: string;
  isRemovable?: boolean;
  hideIconContainer?: boolean;
  info?: IBackendProjectInfoModel;
  isRequired?: boolean;
  iconPosition?: 'left' | 'right';
}) => {
  i18n.fileUpload.labelEnd = '';
  let files: IProjectRepofile[];
  const projectId =
    info !== undefined ? info.id : lens.prop('id').toProps().value;
  const projectName =
    info !== undefined
      ? info.name
      : lens.prop('projectMetadata').toProps().value?.name;
  const labURL =
    info !== undefined
      ? info.labUrl
      : lens.prop('projectMetadata').toProps().value?.labUrl;
  const isFromCreateProj = projectId === 0;
  const [showGitlabPicker, setShowGitlabPicker] = useState(false);
  const { showWarning } = useNotifications();
  const link = getLinkByType(lens, targetLinkType);
  const index = projectLinks.findIndex(
    (projectLink) => projectLink.linkType === targetLinkType
  );
  const [selectedFile, setSelectedFile] = useState<IProjectRepofile | null>(
    null
  );

  const gitlabFilesDataSource = useAsyncDataSource(
    {
      api: async () => {
        const repoFiles = await ProjectManagementService.getProjectRepoFiles(
          projectId
        );
        if (repoFiles?.message) {
          files = [];
        } else {
          const rFiles = repoFiles as unknown as IProjectRepofile[];
          files = rFiles.map((repoFile) => {
            return {
              ...repoFile,
              id: repoFile.lastCommitHash,
            };
          });
        }
        return files;
      },
    },
    []
  );

  const renderRow = (props: DataRowProps<any, any>) => {
    return (
      <DataPickerRow
        {...props}
        key={props.rowKey}
        alignActions="center"
        padding="12"
        renderItem={(item) => {
          const fileType = item.filePath.split('.');
          const _icon =
            fileType[fileType.length - 1].toLowerCase() === 'pdf'
              ? PdfIcon
              : PptIcon;
          const fname =
            item.filename.length > 20
              ? `${item.filename.slice(0, 16)}...`
              : item.filename;
          const _title = `${fname} by ${item.authorName} ${item.latestCommitMessage}`;
          return <PickerItem {...props} icon={_icon} title={_title} />;
        }}
      />
    );
  };
  const crossIcon = isRemovable && (
    <FlexCell alignSelf="center" minWidth={24}>
      <IconButton
        icon={ClearIcon}
        onClick={() => {
          setShowGitlabPicker(false);
          return removeLink(lens, index);
        }}
        cx={!!className && `${className}__remove-button`}
      />
    </FlexCell>
  );
  return link ? (
    <Panel rawProps={{ 'data-auto-scroll': '' }}>
      <FlexRow cx="icon-name-container" vPadding="12" alignItems="top">
        {!hideIconContainer && (
          <FlexCell alignSelf="center" minWidth={24}>
            <IconContainer
              icon={link.link ? done : invalid}
              color={link.link ? 'green' : 'red'}
              size={24}
            />
          </FlexCell>
        )}
        <FlexCell cx="icon-name-container" alignSelf="center" minWidth={150}>
          <LabeledInputWithCustomTooltip
            label={label}
            tooltip={tooltip}
            className={`${className}
            ${
              getLinkProps(lens, index).isInvalid
                ? 'showroom-label__invalid-field'
                : ''
            }
          `}
            isRequired={isRequired}
            iconPosition={iconPosition}
          />
        </FlexCell>
        <FlexCell
          cx="showroom-label__input-block"
          style={{ flexBasis: 'auto' }}
          grow={1}
          shrink={1}
        >
          {link.file ? (
            <FileCard
              file={link.file}
              cx={`uploaded-file ${link.file.size ? '' : 'uploaded-file__old'}`}
              onClick={() => {
                removeLink(lens, projectLinks.indexOf(link));
                addLink(lens, {
                  link: null,
                  linkType: targetLinkType,
                  file: null,
                });
              }}
            />
          ) : showGitlabPicker ? (
            <PickerInput
              dataSource={gitlabFilesDataSource}
              value={selectedFile}
              onValueChange={(newFile) => {
                setSelectedFile(newFile);
                const fileType = newFile?.filePath.split('.') as string[];
                const _fileType = fileType[fileType?.length - 1];
                if (newFile && newFile.size / 1024 / 1024 > 50) {
                  showWarning(DROP_SPOT.FILE_SIZE_EXCEEDED_TEXT);
                } else if (ALLOWED_FILE_EXT_TYPES.includes(_fileType)) {
                  const file = {
                    lastModified: newFile?.lastCommitDate as unknown as number,
                    name: newFile?.filePath as string,
                    webkitRelativePath: '',
                    size: newFile?.size as number,
                    type: fileTypeExtension(_fileType),
                  };
                  removeLink(lens, projectLinks.indexOf(link));
                  addLink(lens, {
                    link: `${labURL}/project-info-materials/-/raw/main/${newFile?.filePath}`,
                    linkType: targetLinkType,
                    file: file as File,
                  });
                } else {
                  showWarning(DROP_SPOT.WRONG_FILE_TYPE_TEXT);
                }
                setShowGitlabPicker(false);
              }}
              entityName="Gitlab File"
              selectionMode="single"
              valueType={'entity'}
              sorting={{ field: 'lastCommitDate', direction: 'desc' }}
              renderRow={renderRow}
              dropdownHeight={180}
            />
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <DropSpot
                  cx={
                    getLinkProps(lens, index).isInvalid
                      ? 'showroom-drop-spot__invalid-field uui-invalid'
                      : `${
                          !isFromCreateProj
                            ? 'showroom-drop-spot__min_width'
                            : 'showroom-drop-spot__max_width'
                        }`
                  }
                  onUploadFiles={(files) => {
                    if (files[0].size / 1024 / 1024 > 50) {
                      showWarning(DROP_SPOT.FILE_SIZE_EXCEEDED_TEXT);
                    } else if (ALLOWED_FILE_TYPES.includes(files[0].type)) {
                      removeLink(lens, projectLinks.indexOf(link));
                      addLink(lens, {
                        link: files[0],
                        linkType: targetLinkType,
                        file: files[0],
                      });
                    } else {
                      showWarning(DROP_SPOT.WRONG_FILE_TYPE_TEXT);
                    }
                  }}
                  infoText={DROP_SPOT.INFO_TEXT}
                />
                {!isFromCreateProj && (
                  <AddGitlabFile
                    onClick={() => setShowGitlabPicker(true)}
                    isInvalid={getLinkProps(lens, index).isInvalid}
                  />
                )}
              </div>
            </>
          )}
        </FlexCell>
        {crossIcon}
      </FlexRow>
      <FlexRow cx="error-msg-section">
        <LabeledInput {...getLinkProps(lens, index)} />
      </FlexRow>
    </Panel>
  ) : (
    <></>
  );
};

export default UploadFile;
